import { EIntakeStep } from "@rentiohq/shared-frontend/dist/types/application.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Icon } from "@rentiohq/web-shared/dist/components";
import { useContext, useEffect, useRef, useState } from "react";
import {
  Droppable,
  DroppableProvided,
  DroppableStateSnapshot,
} from "react-beautiful-dnd";
import {
  DragAndDropContext,
  setDragHoveredTabId,
  markTabLeft,
  setDragFinalDropTarget,
  resetDragState,
  dragHoveredTabId,
  tabLeftAt,
} from "../../context/DragAndDropContext";
import * as S from "./ApplicationTabs.styled";

export interface ITab {
  id: EIntakeStep | "archived";
  label: string;
  count: number;
  /** Arrived on this tab since the broker was last on it (DEV-3272). */
  newCount?: number;
  isDisabled?: boolean;
  phase?: 1 | 2 | 3 | undefined;
}

interface IProps {
  tabs: ITab[];
  activeTab: EIntakeStep | "archived";
  onTabChange: (tabId: EIntakeStep | "archived") => void;
}

export const ApplicationTabs = (props: IProps) => {
  const { tabs, activeTab, onTabChange } = props;
  const { isGlobalDragging, hoveredTab, setHoveredTab, hoveredTabRef } =
    useContext(DragAndDropContext);
  const [confirmedTabId, setConfirmedTabId] = useState<string | null>(null);

  // The release handler is bound once per drag, so it reads the tabs through a
  // ref rather than the closure of the render that bound it.
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  useEffect(() => {
    if (!confirmedTabId) {
      return;
    }

    const timeout = setTimeout(
      () => setConfirmedTabId(null),
      S.DROP_CONFIRM_MS,
    );
    return () => clearTimeout(timeout);
  }, [confirmedTabId]);

  useEffect(() => {
    if (!isGlobalDragging) {
      if (hoveredTabRef.current) setHoveredTab(null);
      resetDragState();
      return;
    }

    resetDragState();

    const handleMouseMove = (e: MouseEvent) => {
      const elements = Array.from(
        document.elementsFromPoint(e.clientX, e.clientY),
      );
      const tabEl = elements.find(el => el.getAttribute("data-tab-id"));
      if (tabEl) {
        const id = tabEl.getAttribute("data-tab-id");
        if (id !== hoveredTabRef.current) setHoveredTab(id);
        setDragHoveredTabId(id);
      } else {
        if (hoveredTabRef.current !== null) setHoveredTab(null);
        if (tabLeftAt === 0 && dragHoveredTabId !== null) {
          markTabLeft();
        }
      }
    };

    // A tab that is not disabled for this drag takes it (DEV-3513): the same
    // permissions that dim the tabs while dragging, so the row only skips the
    // fly-back where the move will actually be made.
    const landsOn = (id: string) => {
      const tab = tabsRef.current.find(
        candidate => String(candidate.id) === id,
      );
      return !!tab && !tab.isDisabled && String(activeTabRef.current) !== id;
    };

    // One release per drag: the first of pointerup and mouseup decides, so the
    // fly-back the row picks and the move onDragEnd makes read the same answer.
    let hasReleased = false;
    const handleRelease = (e: MouseEvent) => {
      if (hasReleased) {
        return;
      }
      hasReleased = true;

      const elements = Array.from(
        document.elementsFromPoint(e.clientX, e.clientY),
      );
      const tabEl = elements.find(el => el.getAttribute("data-tab-id"));

      let target: string | null = null;
      if (tabEl) {
        target = tabEl.getAttribute("data-tab-id");
      } else if (
        dragHoveredTabId &&
        tabLeftAt > 0 &&
        Date.now() - tabLeftAt < 100
      ) {
        target = dragHoveredTabId;
      }

      const lands = !!target && landsOn(target);
      setDragFinalDropTarget(target, lands);
      if (lands) {
        setConfirmedTabId(target);
      }
    };

    // pointerup fires before mouseup, which is what react-beautiful-dnd drops
    // on, so the target is known before the drop animation is chosen. mouseup
    // stays as the fallback for a browser that sends no pointerup.
    window.addEventListener("mousemove", handleMouseMove, { capture: true });
    window.addEventListener("pointerup", handleRelease, { capture: true });
    window.addEventListener("mouseup", handleRelease, { capture: true });
    return () => {
      window.removeEventListener("mousemove", handleMouseMove, {
        capture: true,
      });
      window.removeEventListener("pointerup", handleRelease, { capture: true });
      window.removeEventListener("mouseup", handleRelease, { capture: true });
    };
  }, [isGlobalDragging]);

  const regularTabs = tabs.filter(tab => tab.id !== "archived");
  const archiveTab = tabs.find(tab => tab.id === "archived");

  const phase1Tabs = regularTabs.filter(t => t.phase === 1);
  const phase2Tabs = regularTabs.filter(t => t.phase === 2);
  const phase3Tabs = regularTabs.filter(t => t.phase === 3);
  const grayTabs = regularTabs.filter(t => !t.phase);

  const renderTab = (
    tab: ITab,
    indexInGroup: number,
    globalIndex: number,
    groupSize: number,
  ) => {
    const isActive = activeTab === tab.id;
    const isArchiveTab = tab.id === "archived";
    const isDropDisabled = !!tab.isDisabled || isActive || isArchiveTab;
    const isFirstInGroup = indexInGroup === 0;
    const isLastInGroup = indexInGroup === groupSize - 1;

    return (
      <Droppable
        key={`tab-drop-${tab.id}`}
        droppableId={`tab-${tab.id}`}
        // Never a destination for react-beautiful-dnd: it hit-tests the
        // dragged clone's center, which sits right of the cursor, so a release
        // on a disabled tab flew to an open tab beside it. Where a drop lands
        // is read from the cursor on release instead (handleRelease), so every
        // drop that does not land flies home (DEV-3222, DEV-3513).
        isDropDisabled={true}
        isCombineEnabled={false}
        ignoreContainerClipping={false}
        direction={"horizontal"}
      >
        {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            style={{
              display: "flex",
              alignSelf: "stretch",
              position: "relative",
              zIndex: indexInGroup + 1,
              marginLeft: isFirstInGroup ? "0" : "-12px",
              ...(isArchiveTab ? { flex: 1 } : {}),
            }}
          >
            <S.Tab
              data-tab-id={tab.id}
              isActive={isActive}
              isFirst={isFirstInGroup}
              isLast={isLastInGroup}
              isArchive={isArchiveTab}
              phase={tab.phase}
              isDragOver={
                isGlobalDragging &&
                hoveredTab === String(tab.id) &&
                !isDropDisabled
              }
              isDropDisabled={!!tab.isDisabled}
              $isDropConfirmed={confirmedTabId === String(tab.id)}
              onClick={() => onTabChange(tab.id)}
            >
              {isArchiveTab && <Icon source="archive" size="small" />}
              <S.TabLabel data-label={tab.label}>{tab.label}</S.TabLabel>
              {(tab.count > 0 || !!tab.newCount) && (
                <S.TabBadgeGroup>
                  {tab.count > 0 && (
                    <S.TabBadge isActive={isActive}>{tab.count}</S.TabBadge>
                  )}
                  {!!tab.newCount && (
                    <S.TabNewBadge
                      $isOverlay={tab.count > 0}
                      title={getLocalizedText(
                        tab.newCount === 1
                          ? "applications.tab.new.one"
                          : "applications.tab.new.many",
                        { count: `${tab.newCount}` },
                      )}
                    >
                      +{tab.newCount}
                    </S.TabNewBadge>
                  )}
                </S.TabBadgeGroup>
              )}
            </S.Tab>
            <div style={{ display: "none" }}>{provided.placeholder}</div>
          </div>
        )}
      </Droppable>
    );
  };

  const renderGroup = (groupTabs: ITab[], startIndex: number) => {
    if (groupTabs.length === 0) return null;
    return (
      <S.PhaseGroup key={`group-${groupTabs[0].id}`}>
        {groupTabs.map((tab, idx) =>
          renderTab(tab, idx, startIndex + idx, groupTabs.length),
        )}
      </S.PhaseGroup>
    );
  };

  return (
    <S.TabsContainer isDragging={isGlobalDragging}>
      {renderGroup(phase1Tabs, 0)}
      {renderGroup(phase2Tabs, phase1Tabs.length)}
      {renderGroup(phase3Tabs, phase1Tabs.length + phase2Tabs.length)}
      {renderGroup(
        grayTabs,
        phase1Tabs.length + phase2Tabs.length + phase3Tabs.length,
      )}
      {archiveTab && (
        <div
          key="group-archived"
          style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}
        >
          {renderTab(archiveTab, 0, regularTabs.length, 1)}
        </div>
      )}
    </S.TabsContainer>
  );
};
